import React from 'react';
import { useCalculator } from '../../../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../../../utils/formatters';
import { PaymentCalculatorState } from '../types';
import { Investment } from '../../../types';
import { User, Phone, Mail, UserCheck, DollarSign, Target, Calendar, Percent, TrendingUp, RefreshCw } from 'lucide-react';
import { Toggle } from '../../ui/Toggle';
import ChartSeriesToggleCard from '../../ui/ChartSeriesToggleCard';

interface ConfigurationTabProps {
  state: PaymentCalculatorState;
  onStateUpdate: (updates: Partial<PaymentCalculatorState>) => void;
  investment: Investment;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ state, onStateUpdate, investment }) => {
  const { updateInvestment, toggleReinvest, toggleLemonPrice } = useCalculator();

  return (
    <div className="space-y-8">
      {/* Client Information */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Información del Cliente</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre Completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={state.investorName}
                  onChange={(e) => onStateUpdate({ investorName: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre del inversionista"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={state.investorPhone}
                  onChange={(e) => onStateUpdate({ investorPhone: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+52 55 1234 5678"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={state.investorEmail}
                  onChange={(e) => onStateUpdate({ investorEmail: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="cliente@email.com"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ejecutivo Asignado
              </label>
              <div className="relative">
                <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={state.executiveName}
                  onChange={(e) => onStateUpdate({ executiveName: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Nombre del ejecutivo"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono Ejecutivo
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={state.executivePhone}
                  onChange={(e) => onStateUpdate({ executivePhone: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="+52 55 1000 0615"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Ejecutivo
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={state.executiveEmail}
                  onChange={(e) => onStateUpdate({ executiveEmail: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="ejecutivo@cosechacapital.com"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Parameters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Parámetros de Inversión</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Certificados</label>
            <input
              type="number"
              min="1"
              max="20"
              value={investment.initialCertificates}
              onChange={(e) => updateInvestment({ initialCertificates: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Precio Base</label>
            <input
              type="number"
              min="200000"
              max="300000"
              step="1000"
              value={Math.round(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 250000;
                updateInvestment({ certificateBasePrice: value });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Enganche (%)</label>
            <input
              type="number"
              min="10"
              max="100"
              value={investment.downPaymentPercentage || 30}
              onChange={(e) => updateInvestment({ downPaymentPercentage: parseInt(e.target.value) || 30 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tasa Interés (%)</label>
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              value={investment.financingInterestRate || 0}
              onChange={(e) => updateInvestment({ financingInterestRate: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-sm text-gray-600">Inversión Total</div>
              <div className="text-lg font-bold text-green-700">
                {formatCurrency(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Enganche</div>
              <div className="text-lg font-bold text-blue-700">
                {formatCurrency(investment.initialCertificates * investment.certificateBasePrice * (investment.downPaymentPercentage || 30) / 100, investment.currencyFormat)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">A Financiar</div>
              <div className="text-lg font-bold text-purple-700">
                {formatCurrency(investment.initialCertificates * investment.certificateBasePrice * (100 - (investment.downPaymentPercentage || 30)) / 100, investment.currencyFormat)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Mensualidad</div>
              <div className="text-lg font-bold text-orange-700">
                {formatCurrency((investment.initialCertificates * investment.certificateBasePrice * (100 - (investment.downPaymentPercentage || 30)) / 100) / 48, investment.currencyFormat)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Production Parameters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Parámetros de Producción</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Producción por Hectárea (kg)
              </label>
              <input
                type="number"
                min="15000"
                max="35000"
                step="500"
                value={investment.averageProductionPerHectare}
                onChange={(e) => updateInvestment({ averageProductionPerHectare: parseInt(e.target.value) || 24500 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio por Kg (MXN)
              </label>
              <input
                type="number"
                min="20"
                max="50"
                step="1"
                value={investment.averageSalePricePerKg}
                onChange={(e) => updateInvestment({ averageSalePricePerKg: parseInt(e.target.value) || 30 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tasa de Apreciación (%)
              </label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={investment.appreciationRate}
                onChange={(e) => updateInvestment({ appreciationRate: parseFloat(e.target.value) || 12 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incremento Precio Limón (%)
              </label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={investment.lemonPriceIncrease}
                onChange={(e) => updateInvestment({ lemonPriceIncrease: parseFloat(e.target.value) || 5 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Reinvertir Utilidades</span>
            <Toggle
              checked={investment.reinvestProfits}
              onChange={toggleReinvest}
              label=""
            />
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Incremento Precio Limón</span>
            <Toggle
              checked={investment.increaseLemonPrice}
              onChange={toggleLemonPrice}
              label=""
            />
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-purple-50 rounded-lg">
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-1">Utilidad Anual Calculada</div>
            <div className="text-2xl font-bold text-purple-700">
              {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-purple-600 mt-1">
              {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg × 0.1 × 0.65
            </div>
          </div>
        </div>
      </div>

      {/* Executive Information */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <UserCheck className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Información del Ejecutivo</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Ejecutivo
            </label>
            <div className="relative">
              <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={state.executiveName}
                onChange={(e) => onStateUpdate({ executiveName: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Nombre del ejecutivo"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono Ejecutivo
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={state.executivePhone}
                onChange={(e) => onStateUpdate({ executivePhone: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="+52 55 1000 0615"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Ejecutivo
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={state.executiveEmail}
                onChange={(e) => onStateUpdate({ executiveEmail: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="ejecutivo@cosechacapital.com"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationTab;